import React, { useState } from "react";
import { UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { ALL_MENU_ITEMS } from "constants/common";
import { getMenuAccessKey } from "utils/helper";
import { getDefaultMenuAccess } from "utils/helper";
import iziToast from "izitoast";
import { createCompanyUser } from "actions/companyActions";

const AddUserModal = ({ isAddUserModalOpen, setIsAddUserModalOpen, onSuccess }) => {
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    password: "",
  });

  // Default to all sections enabled so the form passes the "at least one menu
  // item" check out of the box; the admin can untick what they don't want.
  const [menuAccess, setMenuAccess] = useState(getDefaultMenuAccess(true));
  // const [allCompany, setAllCompany] = useState([]);

  const onInputChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleCancel = () => {
    setUserData({
      name: "",
      email: "",
      password: "",
      role: "",
    });
    setMenuAccess(getDefaultMenuAccess(true));
    setErrors({});
    setIsAddUserModalOpen(false);
  };

  const handleMenuAccessChange = (key, view) => {
    setMenuAccess((prev) => ({
      ...prev,
      [key]: { ...prev[key], view },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!userData.name) newErrors.name = "Name is required";
    if (!userData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(userData.email)) newErrors.email = "Email is invalid";
    if (!userData.password) newErrors.password = "Password is required";
    // if (!userData.role) newErrors.role = "Role is required";
    if (Object.values(menuAccess).every((item) => !item.view))
      newErrors.company_menu_access = "Select at least one menu item";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    //     name: userData.name,
    console.log("menuAccess====>", { menuAccess, userData });

    if (validateForm()) {
      setIsLoading(true);
      const company_menu_access = JSON.stringify(menuAccess);
      const response = await createCompanyUser({
        name: userData.name,
        email: userData.email,
        password: userData.password,
        // company_role: userData.role,
        company_menu_access: menuAccess,
      });

      console.log({ response });
      if (response.status) {
        setIsLoading(false);
        iziToast.success({
          title: "Success",
          message: response.message,
          position: "topCenter",
        });
        if (typeof onSuccess === "function") onSuccess();
        handleCancel();
      } else {
        // setErrorMessage(response.message);
        setIsLoading(false);
        iziToast.error({
          title: "Error",
          message: response.message,
          position: "topCenter",
        });
      }
    }
  };

  return (
    <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen} className="bg-black/20">
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[760px] sm:w-full p-0 gap-0 overflow-hidden rounded-xl border border-gray-200/80 shadow-xl bg-white">
        <form onSubmit={handleAddUser}>
          <DialogHeader className="relative px-6 pt-6 pb-5 border-b border-gray-200/80 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-s360-violet rounded-r" aria-hidden />
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-s360-violet-50 text-s360-violet">
                <UserPlus className="h-5 w-5" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-xl font-semibold tracking-tight text-gray-900">
                  Add New User
                </DialogTitle>
                {/* <DialogDescription className="mt-1.5 text-sm text-gray-500 leading-relaxed">
                  Create a new user account with name, email, and password.
                </DialogDescription> */}
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Profile
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="add-name" className="text-sm font-medium text-gray-700">
                    Name
                  </Label>
                  <Input
                    id="add-name"
                    type="text"
                    value={userData.name ?? ""}
                    onChange={(e) => onInputChange("name", e.target.value)}
                    className={`h-10 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0 ${
                      errors?.name ? "border-red-400 focus-visible:ring-red-500/20" : ""
                    }`}
                    placeholder="Enter full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="add-email"
                    type="email"
                    value={userData.email ?? ""}
                    onChange={(e) => onInputChange("email", e.target.value)}
                    className={`h-10 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0 ${
                      errors?.email ? "border-red-400 focus-visible:ring-red-500/20" : ""
                    }`}
                    placeholder="Enter email address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-password" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <Input
                    id="add-password"
                    type="password"
                    value={userData.password ?? ""}
                    onChange={(e) => onInputChange("password", e.target.value)}
                    className={`h-10 bg-white border-gray-200 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0 ${
                      errors?.password ? "border-red-400 focus-visible:ring-red-500/20" : ""
                    }`}
                    placeholder="Enter password"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-gray-100">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Menu Access
              </h3>
              <p className="text-sm text-gray-500">
                Choose which sections the user can view in the app.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ALL_MENU_ITEMS.map((item) => {
                  const key = getMenuAccessKey(item);
                  const Icon = item.icon;
                  const checked = menuAccess[key]?.view ?? true;
                  return (
                    <label
                      key={key}
                      htmlFor={`add-menu-${key}`}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        checked
                          ? "border-s360-violet-100 bg-s360-violet-50/70"
                          : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <Checkbox
                        id={`add-menu-${key}`}
                        checked={checked}
                        onCheckedChange={(isChecked) => handleMenuAccessChange(key, !!isChecked)}
                        className="border-gray-300 data-[state=checked]:bg-s360-violet data-[state=checked]:border-s360-violet shrink-0"
                      />
                      <Icon
                        className={`w-4 h-4 shrink-0 ${checked ? "text-s360-violet" : "text-gray-500"}`}
                      />
                      <span
                        className={`text-sm font-medium ${checked ? "text-gray-900" : "text-gray-700"}`}
                      >
                        {item.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="min-w-[88px] rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? "Adding..." : "Add User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
